import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, inject, provideBrowserGlobalErrorListeners, provideZonelessChangeDetection } from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { TranslateLoader, TranslateService, provideTranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader, provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { firstValueFrom, type Observable } from 'rxjs';
import { providePrimeNG } from 'primeng/config';
import { MSAL_INSTANCE, MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  LogLevel,
  PublicClientApplication,
  type AuthenticationResult,
} from '@azure/msal-browser';
import { routes } from './app.routes';
import { authInterceptor, errorInterceptor, GlobalErrorHandler, APP_PUBLIC_CONFIG } from '#core';
import { type PublicAppConfig } from '#models';
import { MessageService, ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { CvxPreset } from '../primeng-preset';
import { environment } from '#environments';

/**
 * Creates the MSAL PublicClientApplication using configuration injected at
 * bootstrap time. The config values (clientId, authority, redirectUri, scopeUri)
 * are fetched from the ARES API which sources them from Azure Key Vault.
 * No client IDs or tenant IDs are bundled into the Angular application.
 */
function msalInstanceFactory(config: PublicAppConfig): PublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: config.clientId,
      authority: config.authority,
      redirectUri: config.redirectUri,
      postLogoutRedirectUri: config.postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.SessionStorage, // safer than localStorage
    },
    system: {
      loggerOptions: {
        loggerCallback: (_level: LogLevel, message: string, containsPii: boolean) => {
          if (!containsPii && !environment.production) {
            console.debug(`[MSAL] ${message}`);
          }
        },
        logLevel: environment.production ? LogLevel.Error : LogLevel.Warning,
        piiLoggingEnabled: false,
      },
    },
  });
}

/**
 * Processes the Azure AD redirect on every page load.
 * Must run before Angular routing so the auth guard sees the correct account state.
 */
function msalInitializerFactory(
  msalService: MsalService,
): () => Observable<AuthenticationResult | null> {
  return () => msalService.handleRedirectObservable();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withFetch(), withInterceptors([authInterceptor, errorInterceptor])),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: CvxPreset,
        options: {
          darkModeSelector: '.dark', // opt-in dark mode — not active by default
          cssLayer: {
            name: 'primeng',
            order: 'tailwind-base, primeng, tailwind-utilities',
          },
        },
      },
      ripple: true,
    }),
    ...provideTranslateHttpLoader({ prefix: '/locale/i18n/', suffix: '.json' }),
    provideTranslateService({
      loader: { provide: TranslateLoader, useClass: TranslateHttpLoader },
      defaultLanguage: 'en',
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        const translateService = inject(TranslateService);
        return () => firstValueFrom(translateService.use('en'));
      },
      multi: true,
    },
    // ── MSAL / Entra ID ──────────────────────────────────────────────────────
    // APP_PUBLIC_CONFIG is provided in main.ts (pre-bootstrap fetch from Key Vault).
    // msalInstanceFactory reads from it — no IDs are hardcoded here.
    { provide: MSAL_INSTANCE, useFactory: msalInstanceFactory, deps: [APP_PUBLIC_CONFIG] },
    MsalService,
    MsalBroadcastService,
    {
      provide: APP_INITIALIZER,
      useFactory: msalInitializerFactory,
      deps: [MsalService],
      multi: true,
    },
    // ─────────────────────────────────────────────────────────────────────────
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    MessageService,
    ConfirmationService,
    DialogService,
  ],
};

